import { formatCurrency, formatPercent, type Currency } from '../money';
import { matchingRules, resolveApprover, type ApprovalContext, type ApprovalDocument, type ApprovalRuleRow, type StepApprover } from '../permissions/approvals';
import { documentName } from './documentNames';
import type { ApprovalStatusValue } from './statuses';

/**
 * The approval gate (piece 22; decisions 0092, 0192): the pure half of the
 * routing and outcome rules every approvable document shares. Five fields on
 * the document, NotRequired explicit and never null, the ApprovalTier rails
 * before the ApprovalRule chain, the escalation sentence written once at
 * routing, rejection as two writes with a reason, re-approval on edit with the
 * carve-out for a change in the Organization's favour, and the two conversion
 * rules. The service in apps/api reads rows and writes fields; nothing here
 * touches a table.
 */
/**
 * 🔒 The approvable types. The Merge Request is the eighth (decision 1484): piece
 * 10's merge escalates through this Approval Workflow and through no second
 * mechanism, so it carries the axis and its rules sit in the same editor. The
 * File Release is the ninth (decision 8960): a Confidential file's release is
 * decided here, and it carries no amount, as a merge does.
 */
export const APPROVABLE_DOCUMENT_TYPES = ['Quote', 'SalesOrder', 'PurchaseOrder', 'WorkOrder', 'Bill', 'VendorCredit', 'ExpenseReport', 'MergeRequest', 'FileRelease'] as const;
export type ApprovableDocumentType = (typeof APPROVABLE_DOCUMENT_TYPES)[number];

export function isApprovable(documentType: string): documentType is ApprovableDocumentType {
  return (APPROVABLE_DOCUMENT_TYPES as readonly string[]).includes(documentType);
}

/** A Change Order carries the five fields and is not a type of its own: it routes on its parent Sales Order's rules against its own value. */
export function routingDocumentType(documentType: string): ApprovableDocumentType {
  if (documentType === 'ChangeOrder') return 'SalesOrder';
  if (isApprovable(documentType)) return documentType;
  throw new ApprovalGateError(`${documentType} carries no approval axis; the nine approvable types are ${APPROVABLE_DOCUMENT_TYPES.join(', ')}`);
}

export class ApprovalGateError extends Error {}

/** The five fields (decision 0092 plus escalationReason). */
export interface ApprovalFields {
  approvalStatus: ApprovalStatusValue;
  approvedByPersonId: string | null;
  approvedAt: Date | null;
  rejectionReason: string | null;
  escalationReason: string | null;
}

/** The resting state when nothing routes: an explicit value, never null. */
export const NOT_REQUIRED_FIELDS: ApprovalFields = { approvalStatus: 'NotRequired', approvedByPersonId: null, approvedAt: null, rejectionReason: null, escalationReason: null };

/** What the rails and the rules are measured against. A margin nobody can compute is null and clears no margin rail. */
export interface DocumentMeasure {
  total: Currency;
  /** Percent, 0 to 100; null when no line has a sell price. */
  marginPercent: number | null;
  /** Percent, 0 to 100; null or 0 when no line carries a discount. */
  discountPercent: number | null;
}

/** The submitter's ApprovalTier rails (decision 0192); a null rail is not checked. */
export interface TierRails {
  maxDocumentValue: Currency | null;
  minMargin: number | null;
  maxDiscountPercent: number | null;
}

export type RailBreach = { rail: 'maxDocumentValue'; limit: Currency } | { rail: 'minMargin'; limit: number; actual: number | null } | { rail: 'maxDiscountPercent'; limit: number; actual: number };

/** The first rail the document is outside, or null when inside every set rail; an employment with no tier has no rails. */
export function railBreach(measure: DocumentMeasure, rails: TierRails | null): RailBreach | null {
  if (!rails) return { rail: 'maxDocumentValue', limit: { amount: 0, currencyCode: measure.total.currencyCode } };
  if (rails.maxDocumentValue !== null && measure.total.amount > rails.maxDocumentValue.amount) return { rail: 'maxDocumentValue', limit: rails.maxDocumentValue };
  if (rails.minMargin !== null && (measure.marginPercent === null || measure.marginPercent < rails.minMargin)) return { rail: 'minMargin', limit: rails.minMargin, actual: measure.marginPercent };
  const discount = measure.discountPercent ?? 0;
  if (rails.maxDiscountPercent !== null && discount > rails.maxDiscountPercent) return { rail: 'maxDiscountPercent', limit: rails.maxDiscountPercent, actual: discount };
  return null;
}

export function withinRails(measure: DocumentMeasure, rails: TierRails | null): boolean {
  return rails !== null && railBreach(measure, rails) === null;
}

export interface EscalationInput {
  measure: DocumentMeasure;
  breach: RailBreach | null;
  hadRails: boolean;
  documentType: string;
  /** Who the step went to, or why nobody could be reached. */
  routedTo: { label: string } | { unroutable: string };
}

/**
 * The sentence written at routing time and never recomputed for display:
 * "$66,900 exceeds your $25,000 approval limit — escalated to M. Torres".
 */
export function escalationSentence(input: EscalationInput): string {
  const amount = formatCurrency(input.measure.total);
  const reason = (() => {
    if (!input.hadRails || !input.breach) return `${amount} needs approval under the ${documentName(input.documentType)} rules`;
    switch (input.breach.rail) {
      case 'maxDocumentValue':
        return `${amount} exceeds your ${formatCurrency(input.breach.limit)} approval limit`;
      case 'minMargin':
        return input.breach.actual === null
          ? `${amount} carries no margin to measure against your ${formatPercent(input.breach.limit)} floor`
          : `a ${formatPercent(input.breach.actual)} margin is under your ${formatPercent(input.breach.limit)} floor on ${amount}`;
      case 'maxDiscountPercent':
        return `a ${formatPercent(input.breach.actual)} discount is over your ${formatPercent(input.breach.limit)} limit on ${amount}`;
    }
  })();
  if ('unroutable' in input.routedTo) return `${reason} — no approver could be reached (${input.routedTo.unroutable}); held for an administrator`;
  return `${reason} — escalated to ${input.routedTo.label}`;
}

export interface RoutingInput {
  document: ApprovalDocument;
  measure: DocumentMeasure;
  /** The submitter's tier rails, or null for an employment with no tier. */
  rails: TierRails | null;
  rules: readonly ApprovalRuleRow[];
  ctx: ApprovalContext;
  /** The PO margin bypass: every line clearing its floor creates the PO Approved; absent on every other type. */
  bypass?: { cleared: boolean } | null;
  /** Re-approval on edit re-routes with the bypass re-evaluated as an input, never as the writer that wins. */
  disallowBypass?: boolean;
  /**
   * The warnings a document carries into its approval, such as a price-sheet
   * line under the Item's minimum margin (decision 0383). A warned document
   * takes the rule chain whatever the submitter's rails say, and the sentence
   * names each warning ahead of the routing.
   */
  warnings?: readonly string[];
  /**
   * 🔒 A warning that rides the routing and changes nothing about where it
   * lands (decision 3280): the credit warning of an order against an account
   * over its limit or on Credit Hold. The rails still clear the document and no
   * rule still lands it Not required; where it lands Pending the sentence names
   * it ahead of the routing, as a warning is named.
   */
  advisories?: readonly string[];
  /**
   * 🔒 What a request that carries no amount asks, in words (decision 1484).
   * A merge has no money for the rails to clear, so it never lands Not required:
   * it takes the rule chain, and with no rule it is held for an administrator.
   */
  ask?: string;
  labelOf: (approver: StepApprover) => string;
}

export type RoutingOutcome =
  | { approvalStatus: 'NotRequired'; via: 'rails' | 'noRule'; escalationReason: null }
  | { approvalStatus: 'Approved'; via: 'bypass'; escalationReason: null }
  | { approvalStatus: 'Pending'; via: 'rule' | 'warnedNoRule' | 'heldNoRule'; escalationReason: string; awaitingStep: number | null; approver: StepApprover | null; unroutable: boolean };

/** 🔒 The warnings a reason names, each without its closing full stop, so the dash after the last never reads ". —" (T984; decision 10690). */
function joinedWarnings(warnings: readonly string[]): string {
  return warnings.map((w) => {
    const text = w.trim();
    // 🔨 T3624: a dot that ends an abbreviation or an initial belongs to the word and stays; only a sentence's own full stop goes.
    return ENDS_IN_ABBREVIATION.test(text) ? text : text.replace(/\.+$/, '');
  }).join('; ');
}

/**
 * 🔨 T3632: "No" is the abbreviation only before a space and a digit ("Plan No. 5"), which is never the end of a warning, so a "No." or "no." that ends one is a sentence's own full stop and goes, capitalised or not.
 */
const ENDS_IN_ABBREVIATION = /(?:^|[\s(])(?:[A-Za-z]|Inc|Ltd|Co|Corp|St|Mr|Mrs|Ms|Dr|vs|etc|approx)\.$/;

/**
 * 🔒 The sentence a warned document no rule covers is held for an
 * administrator with (decision 0527). It names the warnings and says no
 * approval rule covers the amount, so the person reading the Unroutable group
 * knows both why it is there and what would take it off.
 */
export function unroutableWarnedSentence(warnings: readonly string[], total: Currency, documentType: string): string {
  return `${joinedWarnings(warnings)} — no approval rule covers ${formatCurrency(total)} on a ${documentName(documentType)}; held for an administrator`;
}

/**
 * Where a document lands when it is submitted. Inside the submitter's rails it
 * needs no approval and lands NotRequired (decision 0192); outside any rail the
 * ApprovalRule chain applies; no matching rule is NotRequired, an explicit
 * value; a matched rule whose approver cannot be reached lands Pending as
 * unroutable rather than auto-approving (decision 0380).
 *
 * 🔒 A warned document no rule covers is the one exception to that resting
 * state (decision 0527): the warning refuses the rails, so answering
 * NotRequired would leave a variance nobody was asked about. It lands Pending,
 * unroutable, held for an administrator, and it never invents a chain.
 */
export function routeApproval(input: RoutingInput): RoutingOutcome {
  if (input.ask !== undefined) return routeAsk(input, input.ask);
  const warnings = (input.warnings ?? []).filter((w) => w.trim().length > 0);
  // 🔒 Decision 3280: an advisory is named in a Pending sentence beside the warnings and decides nothing below.
  const named = [...warnings, ...(input.advisories ?? []).filter((w) => w.trim().length > 0)];
  if (input.bypass && input.bypass.cleared && !input.disallowBypass && warnings.length === 0) return { approvalStatus: 'Approved', via: 'bypass', escalationReason: null };
  const breach = railBreach(input.measure, input.rails);
  // 🔨 A warned document is never cleared by the rails: a price under the Item's own floor is the approval-with-warning path (decision 0383).
  if (input.rails && breach === null && warnings.length === 0) return { approvalStatus: 'NotRequired', via: 'rails', escalationReason: null };
  const steps = matchingRules(input.document, input.rules);
  if (steps.length === 0) {
    // 🔒 A warned document never lands NotRequired (decision 0527): with no rule to take, it is held for an administrator.
    if (warnings.length > 0) {
      return {
        approvalStatus: 'Pending',
        via: 'warnedNoRule',
        escalationReason: unroutableWarnedSentence(named, input.measure.total, input.document.documentType),
        awaitingStep: null,
        approver: null,
        unroutable: true,
      };
    }
    return { approvalStatus: 'NotRequired', via: 'noRule', escalationReason: null };
  }
  const first = steps[0]!;
  const approver = resolveApprover(first, input.document, input.ctx);
  const routedTo = approver.kind === 'unroutable' ? { unroutable: approver.reason } : { label: input.labelOf(approver) };
  // 🔨 `hadRails` says whether the submitter had rails to be measured against, and nothing about the warnings
  // (found in review, 2026-09-15): folding them in made a warned document that had broken a real rail say
  // "needs approval under the rules" instead of naming the limit it exceeded, and the warning is already prefixed.
  const sentence = escalationSentence({ measure: input.measure, breach, hadRails: input.rails !== null, documentType: input.document.documentType, routedTo });
  return {
    approvalStatus: 'Pending',
    via: 'rule',
    escalationReason: named.length > 0 ? `${joinedWarnings(named)} — ${sentence}` : sentence,
    awaitingStep: first.stepOrder,
    approver,
    unroutable: approver.kind === 'unroutable',
  };
}

/**
 * A request that carries no amount (decision 1484): never cleared by the rails
 * and never Not required, so a person always decides. The sentence leads with
 * what it asks rather than with a sum of money it does not have.
 */
function routeAsk(input: RoutingInput, ask: string): RoutingOutcome {
  const steps = matchingRules(input.document, input.rules);
  if (steps.length === 0) {
    return {
      approvalStatus: 'Pending', via: 'heldNoRule', awaitingStep: null, approver: null, unroutable: true,
      escalationReason: `${ask} — no approval rule covers a ${documentName(input.document.documentType)}; held for an administrator`,
    };
  }
  const first = steps[0]!;
  const approver = resolveApprover(first, input.document, input.ctx);
  const escalationReason = approver.kind === 'unroutable'
    ? `${ask} — no approver could be reached (${approver.reason}); held for an administrator`
    : `${ask} — escalated to ${input.labelOf(approver)}`;
  return { approvalStatus: 'Pending', via: 'rule', escalationReason, awaitingStep: first.stepOrder, approver, unroutable: approver.kind === 'unroutable' };
}

/**
 * The fields a routing outcome writes, in one write: Pending clears the last
 * rejection's reason, which the audit trail keeps, and carries the new
 * sentence; NotRequired and Approved carry a null sentence. One write, because
 * the column refuses Rejected with no reason and a resubmission is Rejected
 * until the moment it is Pending.
 */
export function fieldsAfterRouting(outcome: RoutingOutcome): ApprovalFields {
  if (outcome.approvalStatus === 'Pending') return { approvalStatus: 'Pending', approvedByPersonId: null, approvedAt: null, rejectionReason: null, escalationReason: outcome.escalationReason };
  if (outcome.approvalStatus === 'Approved') return { approvalStatus: 'Approved', approvedByPersonId: null, approvedAt: null, rejectionReason: null, escalationReason: null };
  return { ...NOT_REQUIRED_FIELDS };
}

/** The three writers of approvalStatus in their precedence: re-approval wins, then the workflow, and the bypass is an input to both. */
export type ApprovalWriter = 'reapproval' | 'workflow' | 'bypass';
const WRITER_PRECEDENCE: readonly ApprovalWriter[] = ['reapproval', 'workflow', 'bypass'];

export function winningWrite<T extends { writer: ApprovalWriter }>(candidates: readonly T[]): T | null {
  for (const writer of WRITER_PRECEDENCE) {
    const found = candidates.find((c) => c.writer === writer);
    if (found) return found;
  }
  return null;
}

/** An edit after approval, as the gate sees it; a change in the Organization's favour keeps the approval (decision 0192). */
export type MaterialChange =
  | { kind: 'unitPrice'; from: number; to: number }
  /** 🔒 The discount as the money it takes off the line, in cents, so a Flat discount is a change (decision 0539). */
  | { kind: 'discountCents'; from: number; to: number }
  | { kind: 'marginPercent'; from: number; to: number }
  | { kind: 'quantity'; from: number; to: number }
  | { kind: 'lineAdded' }
  | { kind: 'lineRemoved' }
  | { kind: 'substitution' }
  /** The unit the quantity is in moved, which moves what was sold (decision 0539). */
  | { kind: 'unit' }
  /** What the line is taxed as moved, which moves what the customer pays (decision 0539). */
  | { kind: 'taxCategory' }
  /** 🔒 A Quote line's Optional mark moved, which moves the total and the lines the customer may take (decision 2650). */
  | { kind: 'optional' }
  | { kind: 'incidental' };

export function inOrganizationsFavour(change: MaterialChange): boolean {
  switch (change.kind) {
    case 'unitPrice':
      return change.to >= change.from;
    case 'discountCents':
      return change.to <= change.from;
    case 'marginPercent':
      return change.to >= change.from;
    case 'incidental':
      return true;
    default:
      return false;
  }
}

export type EditVerdict = 'keep' | 'revert' | 'unchanged';

/**
 * Any edit to an approval-material field after approval reverts the document to
 * Pending, except a change in the Organization's favour or an incidental one;
 * a document not yet approved has nothing to revert.
 */
export function editVerdict(fields: Pick<ApprovalFields, 'approvalStatus'>, changes: readonly MaterialChange[]): EditVerdict {
  if (fields.approvalStatus !== 'Approved') return 'unchanged';
  return changes.every(inOrganizationsFavour) ? 'keep' : 'revert';
}

export interface RejectionWrites {
  approval: Pick<ApprovalFields, 'approvalStatus' | 'rejectionReason' | 'approvedByPersonId' | 'approvedAt'>;
  lifecycle: { status: 'Draft' };
}

/** Rejecting is two writes under the two-axis model, and a rejection requires a reason. */
export function rejectionWrites(reason: string | null | undefined): RejectionWrites {
  if (!reason || reason.trim().length === 0) throw new ApprovalGateError('a rejection requires a reason');
  return { approval: { approvalStatus: 'Rejected', rejectionReason: reason.trim(), approvedByPersonId: null, approvedAt: null }, lifecycle: { status: 'Draft' } };
}

/** Rejected is actionable: edit and resubmit returns it to Pending, and every earlier rejection stays readable in the audit trail. */
export function resubmitAllowed(fields: Pick<ApprovalFields, 'approvalStatus'>): boolean {
  return fields.approvalStatus === 'Rejected';
}

/** Send is refused while the gate holds the document, with the reason on the disabled control; the block is explicit rather than a missing button. */
export function sendRefusal(fields: Pick<ApprovalFields, 'approvalStatus'>): string | null {
  if (fields.approvalStatus === 'Pending') return 'Send is refused while approval is pending';
  if (fields.approvalStatus === 'Rejected') return 'Send is refused on a rejected document; edit and resubmit it';
  return null;
}

/** Where a carried approval came from and the day it is measured on (decision 1690): the Quote's printed number, its good-till day and the day the order is made, each `YYYY-MM-DD`. */
export interface ApprovalCarry {
  fromNumber: string;
  goodTill: string;
  madeOn: string;
}

/**
 * 🔒 The words a carried approval rests on (decision 1690), written onto the
 * order where a routing sentence would stand, so the order's block and the
 * invoice raised from it both say where the approval came from.
 */
export function carriedApprovalSentence(input: { fromNumber: string; goodTill: string; sourceApproved: boolean }): string {
  const source = input.sourceApproved ? input.fromNumber : `${input.fromNumber}, which needed nobody's approval`;
  return `Approval carried from ${source}: made from it unchanged inside its good-till date of ${input.goodTill}`;
}

/**
 * A Sales Order converted unchanged from an Accepted Quote lands Approved with no second approval run; approval attaches to the numbers, not the record.
 * 🔒 It carries only inside the Quote's good-till date (decision 1690, the owner's order of 2026-10-03): an order made after it
 * answers null, rests Not required like any other Draft order, and is routed on its own rules when it is sent.
 * 🔒 The advisories the conversion found, the credit warning among them, open the carried sentence as a Pending
 * landing's open the routing sentence (decision 3580; T2282), and decide nothing about where the order lands.
 */
export function conversionApproval(source: ApprovalFields, sourceStatus: string, unchanged: boolean, carry: ApprovalCarry | null = null, advisories: readonly string[] = []): ApprovalFields | null {
  if (!unchanged || sourceStatus !== 'Accepted') return null;
  if (carry && carry.madeOn > carry.goodTill) return null;
  const carried = carry ? carriedApprovalSentence({ fromNumber: carry.fromNumber, goodTill: carry.goodTill, sourceApproved: source.approvalStatus === 'Approved' }) : null;
  const named = advisories.filter((advisory) => advisory.trim().length > 0);
  const lead = named.length > 0 ? named.join('; ') : null;
  const escalationReason = lead && carried ? `${lead} — ${carried}` : lead ?? carried;
  return { approvalStatus: 'Approved', approvedByPersonId: source.approvedByPersonId, approvedAt: source.approvedAt, rejectionReason: null, escalationReason };
}

/**
 * 🔒 An edit to a document whose approval was carried from the one it was made
 * from (decision 1690): only the tax and the shipping charges may move. A tax
 * category is the one tax change a line carries, and a shipping charge is a
 * line the caller has already set aside; anything else that moves a line,
 * a price or a quantity drops the approval, in the Organization's favour or not,
 * because nobody approved this document's own numbers.
 */
export function carriedEditVerdict(changes: readonly MaterialChange[]): EditVerdict {
  return changes.every((change) => change.kind === 'taxCategory' || change.kind === 'incidental') ? 'keep' : 'revert';
}

/** One Sales Order line beside the Invoice line that bills it. */
export interface LineComparison {
  unitPriceFrom: number;
  unitPriceTo: number;
  discountPercentFrom: number;
  discountPercentTo: number;
  marginPercentFrom: number | null;
  marginPercentTo: number | null;
  substituted?: boolean;
}

/**
 * An automatically created Invoice inherits its Sales Order's approval when
 * every line's unit price, discount and margin are unchanged or better
 * (decision 0192); a substitution or a lower line price reverts it to Pending.
 */
export function inheritedInvoiceApproval(lines: readonly LineComparison[]): 'inherit' | 'revert' {
  for (const line of lines) {
    if (line.substituted) return 'revert';
    if (line.unitPriceTo < line.unitPriceFrom) return 'revert';
    if (line.discountPercentTo > line.discountPercentFrom) return 'revert';
    if (line.marginPercentFrom !== null && (line.marginPercentTo === null || line.marginPercentTo < line.marginPercentFrom)) return 'revert';
  }
  return 'inherit';
}

/** The ChecklistItem kind the submitter is told through on a rejection: rework is work and belongs in the same queue as every other generated task. */
export const APPROVAL_REWORK_KIND = 'ApprovalRework';

export function reworkItemTitle(documentType: string, number: string | null, reason: string): string {
  return `${documentName(documentType)}${number ? ` ${number}` : ''} was rejected: ${reason}`;
}

// ---- Why a document rests where it does, and what a Draft's one primary action reads (T131, T223; decision 0713) ----

/** How the last routing landed, off its own audit row: the rails, no rule, the bypass, a rule, or a warning no rule covers. */
export type RoutingVia = RoutingOutcome['via'];

/** What a routing that held nothing ends on, the words the preview, the block and the History row share (T2003; decision 2482). */
const NEEDS_NOBODY = "so it needs nobody's approval";

/** The lead of the Not required sentence; the History row reads it too, without the amount (T2003). */
export const NO_RULE_LEAD = 'No rule covers this document';

/** The words a document inside the submitter's rails rests on, from *inside* on; the amount goes before them (decision 0192). */
export const RAILS_WHY = `inside the submitter's approval limits, ${NEEDS_NOBODY}`;

/**
 * 🔒 The words a Not required document rests on when no rule matched (T131;
 * decision 0713). They lead with *No rule covers this document* and then say it
 * in the words the held sentence of decision 0527 uses, so the two read as one
 * rule from either side.
 */
export function noRuleSentence(total: Currency, documentType: string): string {
  return `${NO_RULE_LEAD}: no approval rule covers ${formatCurrency(total)} on a ${documentName(documentType)}, ${NEEDS_NOBODY}`;
}

/** The words a document inside the submitter's rails rests on (decision 0192). */
export function railsSentence(total: Currency): string {
  return `${formatCurrency(total)} is ${RAILS_WHY}`;
}

/** The words a Purchase Order the margin bypass cleared rests on. */
export const BYPASS_SENTENCE = 'Every line clears its margin floor, so it is approved without routing';

/**
 * 🔒 The History row's phrase for a routing that wrote no sentence, off the same
 * constants the sentences above are made of so the two cannot drift (T2003). The
 * row keeps no amount, so the phrase names none.
 */
export function routingViaPhrase(via: 'rails' | 'noRule' | 'bypass'): string {
  if (via === 'rails') return `${RAILS_WHY.charAt(0).toUpperCase()}${RAILS_WHY.slice(1)}`;
  if (via === 'bypass') return BYPASS_SENTENCE;
  return `${NO_RULE_LEAD}, ${NEEDS_NOBODY}`;
}

/** The sentence the outcome of one routing reads as, the one writer for a routing and for a preview of one. */
export function routingSentence(outcome: RoutingOutcome, total: Currency, documentType: string): string {
  if (outcome.approvalStatus === 'Pending') return outcome.escalationReason;
  if (outcome.via === 'bypass') return BYPASS_SENTENCE;
  if (outcome.via === 'rails') return railsSentence(total);
  return noRuleSentence(total, documentType);
}

/** What the approval block of a document page reads, and what a Draft's one primary action is. */
export interface ApprovalView {
  /** Why the document rests where it does, in the words the routing wrote; null where nothing can say. */
  reason: string | null;
  /**
   * 🔒 A Draft's one outward action (T223; decision 0713): `submitForApproval`
   * where the gate's dry run would hold it, `send` where nothing would; null
   * where the document is not a Draft waiting on its first move.
   */
  sendAction: 'send' | 'submitForApproval' | null;
  /** Beside Submit for approval, the sentence the routing would write: who it goes to and why. */
  sendHint: string | null;
  /**
   * 🔒 The short line a Pending, Approved or Rejected document carries (T739; decision 2482); null on Not
   * required, which carries none. Absent on an answer that does not say, which draws none either.
   */
  standing?: ApprovalStanding | null;
  /** 🔒 ISO instant of the last routing, so a page shows the sentence as a notice when it moves (decision 2482). */
  routedAt?: string | null;
}

/**
 * 🔒 The parts of the short standing line (T739; decision 2482): who a Pending document waits on, null where
 * it is held for an administrator; who approved and when; who rejected. The page words and dates them.
 */
export type ApprovalStanding =
  | { kind: 'Pending'; awaiting: string | null }
  | { kind: 'Approved'; byName: string | null; at: string | null }
  | { kind: 'Rejected'; byName: string | null };

/**
 * 🔒 The approval block's reason and a Draft's primary action, from the five
 * fields, the last routing's own row and the gate's dry run (T131, T223;
 * decision 0713). A held, rejected or approved document reads the sentence its
 * routing wrote. A Not required document reads why: no rule covers it, or it
 * is inside the submitter's limits. A Draft nobody has routed yet reads what
 * sending it would do, which is also what decides its button.
 */
/**
 * 🔒 The words a rejected document rests on (decision 1691): who rejected it
 * and why, and that it is corrected and resubmitted under the same revision.
 */
export function rejectionSentence(input: { reason: string | null; byName: string | null }): string {
  const by = input.byName ? ` by ${input.byName}` : '';
  return `Rejected${by}: ${input.reason ?? 'no reason was kept'}. Correct it and resubmit it; it keeps its revision until it is sent`;
}

export function approvalView(input: {
  fields: Pick<ApprovalFields, 'approvalStatus' | 'escalationReason'> & { rejectionReason?: string | null };
  /** 🔒 Who rejected a Rejected document, off the rejection's own row (decision 1691); absent or null elsewhere. */
  rejectedByName?: string | null;
  /** The last `document.approvalRouted` row, null where the document has never been routed. */
  lastRouting: { via: string | null; reason: string | null } | null;
  /** The gate's dry run, asked for a Draft alone. */
  preview: RoutingOutcome | null;
  /** Whether the document is a Draft waiting on its first outward move. */
  awaitingSend: boolean;
  total: Currency;
  documentType: string;
}): ApprovalView {
  const { fields, lastRouting, preview } = input;
  const routed = lastRouting ? routingSentenceOf(lastRouting, input.total, input.documentType) : null;
  if (fields.approvalStatus === 'Pending') return { reason: fields.escalationReason ?? routed, sendAction: null, sendHint: null };
  if (fields.approvalStatus === 'Rejected') {
    // 🔒 A rejected Draft is resubmitted from its one primary, labelled by the dry run on the corrected numbers (decision 1691).
    const reason = rejectionSentence({ reason: fields.rejectionReason ?? null, byName: input.rejectedByName ?? null });
    if (!input.awaitingSend || !preview) return { reason, sendAction: null, sendHint: null };
    const holds = preview.approvalStatus === 'Pending';
    return { reason, sendAction: holds ? 'submitForApproval' : 'send', sendHint: holds ? preview.escalationReason : null };
  }
  if (fields.approvalStatus === 'Approved') {
    return { reason: fields.escalationReason ?? routed, sendAction: input.awaitingSend ? 'send' : null, sendHint: null };
  }
  // Not required: routed and cleared, or a Draft the gate has not ruled on yet, whose dry run says what sending does.
  if (input.awaitingSend && preview) {
    const holds = preview.approvalStatus === 'Pending';
    return { reason: routingSentence(preview, input.total, input.documentType), sendAction: holds ? 'submitForApproval' : 'send', sendHint: holds ? preview.escalationReason : null };
  }
  return { reason: routed ?? noRuleSentence(input.total, input.documentType), sendAction: input.awaitingSend ? 'send' : null, sendHint: null };
}

/**
 * 🔒 What an outside login reads of an approval (decision 3280; review B1): its status alone, the short
 * standing line, and never the routing's sentence. The sentence names the submitter's limits, the approver
 * and the credit warning written for the salesperson, which a customer's portal login is never sent.
 */
export function outsideApprovalView(view: ApprovalView): ApprovalView {
  return { ...view, reason: null, sendHint: null };
}

function routingSentenceOf(row: { via: string | null; reason: string | null }, total: Currency, documentType: string): string | null {
  if (row.reason) return row.reason;
  if (row.via === 'bypass') return BYPASS_SENTENCE;
  if (row.via === 'rails') return railsSentence(total);
  if (row.via === 'noRule') return noRuleSentence(total, documentType);
  return null;
}

/**
 * 🔒 The one approval rule seeded at install (T131; decision 0713), so the gate
 * does something on a fresh database: a Quote of $25,000.00 or more goes to the
 * Admin role. It is written only where the Quote has no rule at all, so an
 * administrator's own edit or deactivation stands. 💰 Self-approval is allowed
 * on it, because a fresh install holds one person, and a rule that person could
 * not satisfy would hold their first large Quote for ever.
 */
export const SEEDED_QUOTE_APPROVAL_RULE = {
  documentType: 'Quote',
  minAmountCents: 2_500_000,
  currencyCode: 'USD',
  stepOrder: 1,
  approverRoleName: 'Admin',
  allowSelfApproval: true,
} as const;

/**
 * 🔒 The Invoice's approval block (T131; decision 0713). An Invoice carries no
 * approval of its own (decision 0503): it reads the order behind it, in that
 * order's own words, and an invoice no order stands behind is covered by no rule.
 */
export function invoiceApprovalReason(order: { number: string; reason: string | null; approvalStatus?: ApprovalStatusValue; approvedByName?: string | null } | null): string {
  if (!order) return 'No rule covers this document: an invoice carries no approval of its own, and no order stands behind this one';
  // 🔒 An approved order clears the invoice raised from it (decision 1690): the sentence says so and names who approved it.
  if (order.approvalStatus === 'Approved') {
    const by = order.approvedByName ? ` by ${order.approvedByName}` : '';
    return `The approval is the order's, ${order.number}: approved${by}${order.reason ? ` (${order.reason})` : ''}, so this invoice needs no approval of its own`;
  }
  return `The approval is the order's, ${order.number}: ${order.reason ?? 'the order has not been ruled on yet'}`;
}
